import flatMap from 'lodash/flatMap';
import sortBy from 'lodash/sortBy';

import { OPEN_HOURS_FLEXIBILITY } from '../modules/public/enquiry-form/store/types';

export const HIGHEST_TIME_SLOT = 49;

export const HOURS_MAP_24 = Object.freeze({
  1: '06:00',
  2: '06:30',
  3: '07:00',
  4: '07:30',
  5: '08:00',
  6: '08:30',
  7: '09:00',
  8: '09:30',
  9: '10:00',
  10: '10:30',
  11: '11:00',
  12: '11:30',
  13: '12:00',
  14: '12:30',
  15: '13:00',
  16: '13:30',
  17: '14:00',
  18: '14:30',
  19: '15:00',
  20: '15:30',
  21: '16:00',
  22: '16:30',
  23: '17:00',
  24: '17:30',
  25: '18:00',
  26: '18:30',
  27: '19:00',
  28: '19:30',
  29: '20:00',
  30: '20:30',
  31: '21:00',
  32: '21:30',
  33: '22:00',
  34: '22:30',
  35: '23:00',
  36: '23:30',
  37: '00:00',
  38: '00:30',
  39: '01:00',
  40: '01:30',
  41: '02:00',
  42: '02:30',
  43: '03:00',
  44: '03:30',
  45: '04:00',
  46: '04:30',
  47: '05:00',
  48: '05:30',
  49: '06:00',
});

export const HOURS_MAP_30 = Object.freeze({
  1: '06:00',
  2: '06:30',
  3: '07:00',
  4: '07:30',
  5: '08:00',
  6: '08:30',
  7: '09:00',
  8: '09:30',
  9: '10:00',
  10: '10:30',
  11: '11:00',
  12: '11:30',
  13: '12:00',
  14: '12:30',
  15: '13:00',
  16: '13:30',
  17: '14:00',
  18: '14:30',
  19: '15:00',
  20: '15:30',
  21: '16:00',
  22: '16:30',
  23: '17:00',
  24: '17:30',
  25: '18:00',
  26: '18:30',
  27: '19:00',
  28: '19:30',
  29: '20:00',
  30: '20:30',
  31: '21:00',
  32: '21:30',
  33: '22:00',
  34: '22:30',
  35: '23:00',
  36: '23:30',
  37: '24:00',
  38: '24:30',
  39: '25:00',
  40: '25:30',
  41: '26:00',
  42: '26:30',
  43: '27:00',
  44: '27:30',
  45: '28:00',
  46: '28:30',
  47: '29:00',
  48: '29:30',
  49: '30:00',
});

export const HOURS_MAP_12 = Object.freeze({
  1: '6:00 am',
  2: '6:30 am',
  3: '7:00 am',
  4: '7:30 am',
  5: '8:00 am',
  6: '8:30 am',
  7: '9:00 am',
  8: '9:30 am',
  9: '10:00 am',
  10: '10:30 am',
  11: '11:00 am',
  12: '11:30 am',
  13: '12:00 pm',
  14: '12:30 pm',
  15: '1:00 pm',
  16: '1:30 pm',
  17: '2:00 pm',
  18: '2:30 pm',
  19: '3:00 pm',
  20: '3:30 pm',
  21: '4:00 pm',
  22: '4:30 pm',
  23: '5:00 pm',
  24: '5:30 pm',
  25: '6:00 pm',
  26: '6:30 pm',
  27: '7:00 pm',
  28: '7:30 pm',
  29: '8:00 pm',
  30: '8:30 pm',
  31: '9:00 pm',
  32: '9:30 pm',
  33: '10:00 pm',
  34: '10:30 pm',
  35: '11:00 pm',
  36: '11:30 pm',
  37: '12:00 am',
  38: '12:30 am',
  39: '1:00 am',
  40: '1:30 am',
  41: '2:00 am',
  42: '2:30 am',
  43: '3:00 am',
  44: '3:30 am',
  45: '4:00 am',
  46: '4:30 am',
  47: '5:00 am',
  48: '5:30 am',
  49: '6:00 am',
});

export const TIME_RANGE_START = 1;

export function getTimeSlots(timeFormat) {
  if (timeFormat === 12) {
    return HOURS_MAP_12;
  }
  if (timeFormat === 24) {
    return HOURS_MAP_24;
  }

  throw new Error(`Invalid timeFormat: ${timeFormat}`);
}

export function getTimeLabel(timeInteger, timeFormat) {
  if (timeFormat === 12) {
    return HOURS_MAP_12[timeInteger];
  }
  if (timeFormat === 24) {
    return HOURS_MAP_24[timeInteger];
  }

  throw new Error(`Invalid timeFormat: ${timeFormat}`);
}

export const DEFAULT_EVENT_DURATION_HOURS = 4;

// Only simplified pricing sets a minimum (2h by default), so a set value doubles as the prefill duration;
// custom-priced rooms have none and fall back to the default.
export function getDefaultEventDurationSlots(room) {
  return (room?.minHoursForBooking || DEFAULT_EVENT_DURATION_HOURS) * 2;
}

export function getMaximumSelectableToSlot(openedTimeSlots, blockedTimeSlots, testTimeFrom, maxTimeTo) {
  if (!blockedTimeSlots.length) {
    return maxTimeTo;
  }

  const minValue = testTimeFrom + 1;
  let maxValue = maxTimeTo;

  blockedTimeSlots.forEach(([timeFrom]) => {
    if (minValue <= timeFrom) {
      maxValue = Math.min(maxValue, timeFrom);
    }
  });

  if (openedTimeSlots[openedTimeSlots.length - 1] <= maxValue) {
    maxValue = openedTimeSlots[openedTimeSlots.length - 1];
  }
  if (maxTimeTo <= maxValue) {
    return maxTimeTo; // keep original value
  }
  return maxValue; // new max value
}

export function getRoomOpenHours(date, roomAvailability) {
  if (!date || roomAvailability?.hoursFlexibility === OPEN_HOURS_FLEXIBILITY.FLEXIBLE) {
    return [1, 49];
  }
  if (!roomAvailability?.openingHours) {
    return [];
  }
  const selectedDayOfWeek = window.dayjs(date, 'DD/MM/YYYY').day();
  const roomOpenHours = sortBy(flatMap(roomAvailability.openingHours[selectedDayOfWeek]));

  // If there is a break in open hours, we let users enquire anyway
  return [roomOpenHours[0], roomOpenHours[roomOpenHours.length - 1]];
}

export function getBlockedTimeSlots(date, roomAvailability) {
  if (!roomAvailability?.partiallyBookedDays) {
    return [];
  }
  const selectedDate = window.dayjs(date, 'DD/MM/YYYY').format('YYYY-MM-DD');
  return roomAvailability.partiallyBookedDays[selectedDate] ?? [];
}

export function isRoomClosed(date, roomAvailability) {
  if (date) {
    const dateYMD = window.dayjs(date, 'DD/MM/YYYY').format('YYYY-MM-DD');
    return roomAvailability.closedDaysOfWeek.includes(window.dayjs(date, 'DD/MM/YYYY').day())
        || roomAvailability.fullyBookedDays.includes(dateYMD);
  }
  return false;
}

function inRange(x, min, max) {
  return ((x - min) * (x - max) <= 0);
}

export function isRoomAvailableInSelectedHours(date, timeFrom, timeTo, roomAvailability) {
  if (isRoomClosed(date, roomAvailability)) {
    return false;
  }
  const roomOpenHours = getRoomOpenHours(date, roomAvailability);
  if (!inRange(timeFrom, roomOpenHours[0], roomOpenHours[1]) || !inRange(timeTo, roomOpenHours[0], roomOpenHours[1])) {
    return false;
  }

  let result = true;
  const blockedTimeSlots = getBlockedTimeSlots(date, roomAvailability);
  blockedTimeSlots.forEach(([a, b]) => {
    if (inRange(timeFrom, a, b) || inRange(timeTo, a, b) || inRange(a, timeFrom, timeTo) || inRange(b, timeFrom, timeTo)) {
      result = false;
    }
    return result;
  });

  return result;
}

export function mapRoomAvailability(payload) {
  const daysNameMap = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  };
  const closedDaysOfWeek = Object.keys(payload.openingHours)
    .filter(dayName => !payload.openingHours[dayName].length)
    .map(dayName => daysNameMap[dayName]);

  const openingHours = Object.keys(payload.openingHours)
    .reduce((acc, dayName) => ({
      ...acc,
      [daysNameMap[dayName]]: payload.openingHours[dayName],
    }), {});

  return {
    partiallyBookedDays: payload.partiallyBookedDays,
    fullyBookedDays: payload.fullyBookedDays,
    closedDaysOfWeek,
    openingHours,
  };
}

export function transformTimeRangesToArray(timeRanges) {
  if (!timeRanges) {
    return [];
  }
  return Object.keys(timeRanges)
    .reduce((acc, day) => {
      return {
        ...acc,
        [day]: timeRanges[day].map(({ timeFrom, timeTo }) => {
          return [timeFrom, timeTo];
        }),
      };
    }, {});
}
